import React, { useCallback } from 'react';
import { FormGroup, CheckBox, FormSelect } from '~/components/form';
import { AiFillProfile } from 'react-icons/ai';
import { MdAddCircle, MdRemoveCircle } from 'react-icons/md';
import { loanType, mortgageClause, requiredALTA } from '~/api';
import { SectionHeader } from '../../../PolicySearchRequest/common';
import Insured from './Insured';
import Lender from './Lender';
import { useForm } from '~/hooks';
import { useSectionLoader, useAutoSave, useAutoSaveMultiple, useDeleteEntireSectionDetail } from '../../hooks';
import sectionTypes from '../sectionTypes';

/**
 * @typedef TitleInsuranceApplicationValues
 * @property {number}   sectionId
 * @property {string}   fileNumber 'Your File Number'
 * @property {Date}     estimatedClosingDate 
 * @property {object}   transactionType 
 * @property {[object]} transactionTypeOptions 
 * @property {object}   titleCompany
 * @property {[object]} titleCompanyOptions
 * @property {object}   useOfProperty
 * @property {[object]} useOfPropertyOptions
 * @property {object}   interestOrEstate
 * @property {[object]} interestOrEstateOptions
 * @property {string}   city
 * @property {string}   county
 * @property {object}   state
 * @property {[object]} stateOptions
 * @property {string}   zipcode
 * @property {[object]} initialDefaultData
 * 
 * @typedef FormState
 * @property {TitleInsuranceApplicationValues} values The current form values
 */

/** @type {TitleInsuranceApplicationValues} */
const defaultState = {
    sectionId: 0,
    fileNumber: '',
    estimatedClosingDate: null,
    mortgageClausePrimary: null,
    mortgageClauseSecondary: { label: '...', value: '' },
    mortgageClauseOptions: [],
    loanTypePrimary: { label: '...', value: '' },
    loanTypeSecondary: { label: '...', value: '' },
    loanTypeOptions: [],
    requiredALTAPrimary: null,
    requiredALTASecondary: null,
    requiredALTAOptions: [],    
    addInsured: false,
    sameAsOwner: null,
    insuredSectionDetails: {},
    primaryLenderSectionDetails: {},
    secondaryLenderSectionDetails: {},
    secondaryLenderFlag: false,
    separateCommittment: 'No',
    isClosingProtectionLetterRequiredSecondary: 'Yes',
    isClosingProtectionLetterRequired: 'Yes',
    isPrivateLender: 'No',
    isPrivateLenderSecondary: 'No',
    needOwnerCoverage: null,
    initialDefaultData: {},
    issueHomeownersPolicy: false
};

const loadData = async (values) => {

    const mortgageClauseOptions = (await mortgageClause.getMortgageClauses()).map(f => ({
        label: f.label, value: f.value
    }));

    const loanTypeOptions = (await loanType.getLoanTypes()).map(f => ({
        label: f.label, value: f.value
    }));

    const requiredALTAOptions = (await requiredALTA.getRequiredALTAs()).map(f => ({
        label: f.label, value: f.value
    }));
    
    const ltPrimary = loanTypeOptions.filter(x => x.label === 'Conventional')[0];
    //const lTSecondary = loanTypeOptions.filter(x => x.label === 'Conventional')[0]; 

    return {
        ...values,
        mortgageClauseOptions,
        loanTypeOptions,
        requiredALTAOptions,
        loanTypePrimary: ltPrimary,
        initialDefaultData: {
            needOwnerCoverage: 'Yes',
            isClosingProtectionLetterRequired: 'Yes',
            isPrivateLender: 'No',
            loanTypePrimary: ltPrimary ? ltPrimary : {label: 'Conventional', value: '1'},
            loanNumberPrimary: ''
        }
    };
};

const loadSectionDetails = async (values) => {

    const insuredSectionDetails = values.sectionDetails ? values.sectionDetails.filter(f => f._data.type === 'insured') : defaultState.insuredSectionDetails;
    const primaryLenderSectionDetails = values.sectionDetails ? values.sectionDetails.filter(f => f._data.type === 'lenderPrimary') : defaultState.primaryLenderSectionDetails;
    const secondaryLenderSectionDetails = values.sectionDetails ? values.sectionDetails.filter(f => f._data.type === 'lenderSecondary') : defaultState.secondaryLenderSectionDetails;
    const transactionType = values.additionalSection ? values.additionalSection.transactionType : null;
    const cashSale = values.additionalSection && values.additionalSection.cashSale;
    const sameAsOwner =  !(values.sameAsOwner) ? 
        (Object.keys(insuredSectionDetails).length === 0 && values.additionalSection &&
            values.additionalSection.transactionType && 
            (values.additionalSection.transactionType.label == 'Refinance' || values.additionalSection.transactionType.label == 'Equity')
            ? 
            true 
            : 
            false) 
        : values.sameAsOwner;
    values.initialDefaultData.sameAsOwner = sameAsOwner;
    
    const needOwnerCoverage = values.needOwnerCoverage?.length > 0
        ? values.needOwnerCoverage 
        : values.additionalSection?.transactionType?.label == 'Refinance' 
            ? 'No' 
            : 'Yes';
    
    values.initialDefaultData.needOwnerCoverage = needOwnerCoverage;

    return {
        ...values,
        transactionType,
        cashSale,
        insuredSectionDetails,
        primaryLenderSectionDetails,
        secondaryLenderSectionDetails,
        sameAsOwner,
        needOwnerCoverage,
        initialDefaultData: values.initialDefaultData      
    };
};

const TitleInsuranceApplication = ({ titleOpinionId , readOnly }) => {
    const { onLoad: loadSection } = useSectionLoader(sectionTypes.TITLE_INSURANCE_APPLICATION, titleOpinionId, sectionTypes.PROPERTY_INFO);

    const onLoad = useCallback((values) => {
        return loadData(values)
            .then(v => loadSection(v)
                .then(result => loadSectionDetails(result)));                   
    }, [titleOpinionId]);

    const {
        /** @type {FormState} */
        state,
        fieldActions
    } = useForm({
        /** @type {PropertyInfoValues} */
        initialState: { ...defaultState },
        onLoad,
        debug: true
    });
    
    const { saveField } = useAutoSave(sectionTypes.TITLE_INSURANCE_APPLICATION, titleOpinionId, state.values.initialDefaultData, state.values.sectionId);
    const { deleteEntireSectionDetail } = useDeleteEntireSectionDetail();
    const { saveMulitpleField } = useAutoSaveMultiple(sectionTypes.PROPERTY_INFO, titleOpinionId, state.values.initialDefaultData, state.values.sectionId);

    const onBlur = (value, path, validator) => {
        saveField(value, path, state.values.initialDefaultData, state.values.sectionId);
        return fieldActions.onBlur(value, path, validator);
    };

    const onChangeHandler = (event) => {
        saveField(event.target.value, event.target.name, state.values.initialDefaultData, state.values.sectionId);
        fieldActions.onChange(event.target.value, event.target.name); 
    };    

    const addSecondLender = async () => {
        const data = {            
            isClosingProtectionLetterRequiredSecondary: 'No',
            isPrivateLenderSecondary: 'No',
            separateCommittment: 'No',            
            mortgageClauseSecondary: defaultState.mortgageClauseOtherSecondary,
            requiredALTASecondary: defaultState.requiredALTASecondary,
            loanTypeSecondary: state.values.loanTypeSecondary,
            lenderInsuranceCoverageAmountSecondary: '',
            loanNumberSecondary: '',
            mortgageClauseOtherSecondary: '',
            requiredALTAOtherSecondary: '',
            secondaryLenderFlag: true,
        };
        await saveMulitpleField(data, state.values.initialDefaultData, state.values.sectionId);
        fieldActions.onChange(true, 'secondaryLenderFlag');
    };
    const removeSecondLender = async () => {
        const data = {
            secondaryLenderFlag: false,
            isClosingProtectionLetterRequiredSecondary: 'No',
            isPrivateLenderSecondary: 'No',
            separateCommittment: 'No',
            mortgageClauseSecondary: defaultState.mortgageClauseOtherSecondary,
            requiredALTASecondary: defaultState.requiredALTASecondary,
            loanTypeSecondary: defaultState.loanTypeSecondary,
            lenderInsuranceCoverageAmountSecondary: '',
            loanNumberSecondary: '',
            mortgageClauseOtherSecondary: '',
            requiredALTAOtherSecondary: ''
            
        };
        await saveMulitpleField(data, state.values.initialDefaultData, state.values.sectionId);
        await deleteEntireSectionDetail(state.values.secondaryLenderSectionDetails); 
        return fieldActions.onChange(false, 'secondaryLenderFlag');
    };

    return (
        <div className="ml-5 mr-5">
            <div className="mb-5">
                <div>
                    <div className="z-10 flex items-center h-full text-xl font-semibold text-blue-60">
                        <div className="h-11 w-11 rounded-md flex items-center justify-center bg-blue-30"><AiFillProfile size="45%"/></div>
                        <div className="ml-2"> Title Insurance Application </div>
                    </div> 
                    
                </div>
            </div>
            <div className="text-sm mb-5">Please note any information changes will overwrite any previously entered. Changes are saved automatically.</div>            
                
            <div className="col-span-8 grid grid-cols-12 grid-flow-row gap-x-8 gap-y-4 text-sm mb-4">
                <div className="col-span-12 flex flex-direction:row gap-x-5 gap-y-4 mb-4">
                    <p className="font-medium text-neutral-90">
                    Do you need owner&apos;s coverage ? 
                    </p>
                    <input type="radio" value="Yes" name="needOwnerCoverage" 
                        checked={state.values.needOwnerCoverage === 'Yes'}
                        disabled={readOnly || (state.values.transactionType && state.values.transactionType.label === 'Purchase')}
                        onChange={onChangeHandler}
                        
                    /> Yes
                    <input type="radio" value="No" name="needOwnerCoverage"
                        checked={state.values.needOwnerCoverage === 'No'}
                        disabled={readOnly || (state.values.transactionType && state.values.transactionType.label === 'Purchase')}
                        onChange={onChangeHandler}
                    /> No
                </div>
                <div className="col-span-12 flex flex-direction:row gap-x-5 gap-y-4 mb-4">
                    <CheckBox 
                        label="Issue Homeowners Policy?"
                        path="issueHomeownersPolicy"
                        groupClassName="col-span-12 flex flex-row-reverse items-center gap-x-4"
                        value={state.values.issueHomeownersPolicy}
                        onChange={(issueHomeownersPolicy) =>{
                            saveField(issueHomeownersPolicy, 'issueHomeownersPolicy', state.values.initialDefaultData, state.values.sectionId);
                            fieldActions.onChange(issueHomeownersPolicy, 'issueHomeownersPolicy');                     
                        }}
                    />
                </div>
                {state.values.needOwnerCoverage === 'Yes' ? 
                    <div className="col-span-12 grid grid-cols-12 grid-flow-row gap-x-8 gap-y-4 text-sm mb-4">
                        <div className="col-span-4 font-medium text-neutral-90"> Owner&apos;s Insurance Coverage Amount (Purchase Price) *</div>
                        <FormGroup
                            groupClassName="col-span-8"
                            type='currency'
                            path='ownerInsuranceCoverageAmount'    
                            readOnly={readOnly}             
                            value={state.values.ownerInsuranceCoverageAmount}
                            error={state.errors.ownerInsuranceCoverageAmount}
                            {...fieldActions}
                            onBlur={onBlur}
                        />
                        <SectionHeader colSpan={12}>Insured*</SectionHeader>
                        {state.values.transactionType && state.values.transactionType.label === 'Purchase' ?
                            <Insured colSpan={12} state={state} fieldActions={fieldActions} titleOpinionId={titleOpinionId} sectionId={state.values.sectionId} sectionDetails={state.values.insuredSectionDetails} initialDefaultData={state.values.initialDefaultData} editMode={!readOnly}/>
                            :
                            <>
                                <CheckBox 
                                    label="Same as seller/ current owner(s)?"
                                    path="sameAsOwner"
                                    readOnly={readOnly}
                                    groupClassName="col-span-12 flex items-center text-sm"
                                    value={state.values.sameAsOwner}
                                    onChange={(sameAsOwner) =>{                            
                                        saveField(sameAsOwner, 'sameAsOwner', state.values.initialDefaultData, state.values.sectionId);
                                        fieldActions.onChange(sameAsOwner, 'sameAsOwner');
                                        if(sameAsOwner && state.values.insuredSectionDetails && state.values.insuredSectionDetails.length !== 0) {
                                            deleteEntireSectionDetail(state.values.insuredSectionDetails);
                                        } 
                                                                    
                                    }}
                                />
                                {state.values.sameAsOwner ? 
                                    <></> 
                                    :
                                    <>
                                        <Insured colSpan={12} state={state} fieldActions={fieldActions} titleOpinionId={titleOpinionId} sectionId={state.values.sectionId} sectionDetails={state.values.insuredSectionDetails} initialDefaultData={state.values.initialDefaultData} editMode={!readOnly}/>
                                    </>  
                                }
                            </>
                        }
                        
                    </div>
                    :
                    <></>
                }
                
                
                {/* Lender(s) */}
                {state.values.transactionType && state.values.transactionType.label == 'Purchase' && state.values.additionalSection.cashSale ? 
                    <div className="col-span-12 grid grid-cols-6 grid-flow-row gap-x-8 gap-y-4 text-sm mb-4 mt-4">
                        <div className="col-span-6 flex flex-direction:row gap-x-5 gap-y-4">
                            <p className="font-medium text-neutral-90">
                            Is a Closing Protection Letter Required ? 
                            </p>
                            <input type="radio" value="Yes" name="isClosingProtectionLetterRequired" 
                                checked={state.values.isClosingProtectionLetterRequired === 'Yes'}
                                disabled={readOnly}
                                //className="disabled:bg-neutral-70"
                                onChange={onChangeHandler}
                                
                            /> Yes
                            <input type="radio" value="No" name="isClosingProtectionLetterRequired"
                                checked={state.values.isClosingProtectionLetterRequired === 'No'}
                                disabled={readOnly}
                                //className="disabled:bg-neutral-70"
                                onChange={onChangeHandler}
                            /> No
                        </div>
                        {state.values.requiredALTAPrimary.label == 'Other' ?
                            <>
                                <FormSelect
                                    label="Required ALTA Endorsements"
                                    type='select'
                                    options={state.values.requiredALTAOptions}
                                    readOnly={readOnly}
                                    isClearable={false}
                                    isSearchable
                                    isMulti = {true}
                                    closeOnSelect={false}
                                    required={!state.values.additionalSection.cashSale}
                                    groupClassName="col-span-4 mt-4"
                                    path='requiredALTAPrimary'   
                                    value={state.values.requiredALTAPrimary}
                                    error={state.errors.requiredALTAPrimary}
                                    onChange={async (requiredALTA) => {
                                        saveField(requiredALTA, 'requiredALTAPrimary', state.values.initialDefaultData, state.values.sectionId);
                                        fieldActions.onChange(requiredALTA, 'requiredALTAPrimary');
                                        fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');                        
                                    }}
                                    onBlur={onBlur}             
                                />
                                <FormGroup
                                    groupClassName="col-span-2 mt-4 mb-4"
                                    path='requiredALTAOtherPrimary' 
                                    readOnly={readOnly}                
                                    value={state.values.requiredALTAOtherPrimary}
                                    error={state.errors.requiredALTAOtherPrimary}
                                    {...fieldActions}
                                    onBlur={onBlur}
                                />
    
                            </>
    
                            :
                            <FormSelect
                                label="Required ALTA Endorsements"
                                type='select'
                                options={state.values.requiredALTAOptions}
                                required={!state.values.additionalSection.cashSale}
                                readOnly={readOnly}
                                isClearable={false}
                                isSearchable
                                closeOnSelect={false}
                                isMulti = {true}
                                groupClassName="col-span-3"
                                path='requiredALTAPrimary'   
                                value={state.values.requiredALTAPrimary}
                                error={state.errors.requiredALTAPrimary}
                                onChange={async (requiredALTA) => {
                                    saveField(requiredALTA, 'requiredALTAPrimary', state.values.initialDefaultData, state.values.sectionId);
                                    fieldActions.onChange(requiredALTA, 'requiredALTAPrimary');
                                    fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');                        
                                }}
                                onBlur={onBlur}             
                            />
                        }
                    </div>
                    :
                    <>
                        <div className="col-span-12 border-t-2 border-blueGray-30 mt-4"/>
                        <SectionHeader colSpan={12}>Lender</SectionHeader>
                        <div className="col-span-12 grid grid-cols-12 grid-flow-row gap-x-8 gap-y-4 text-sm mb-4">
                            <div className="col-span-6 flex flex-direction:row gap-x-5 gap-y-4">
                                <p className="font-medium text-neutral-90">
                                Is this a private lender ? 
                                </p>
                                <input type="radio" value="Yes" name="isPrivateLender" 
                                    checked={state.values.isPrivateLender === 'Yes'}
                                    disabled={readOnly}
                                    //className="disabled:bg-neutral-70"
                                    onChange={onChangeHandler}
                                    
                                /> Yes
                                <input type="radio" value="No" name="isPrivateLender"
                                    checked={state.values.isPrivateLender === 'No'} 
                                    disabled={readOnly}
                                    //className="disabled:bg-neutral-70"
                                    onChange={onChangeHandler}
                                /> No
                                
                            </div>
                            <div className="col-span-6 flex flex-direction:row gap-x-5 gap-y-4">
                                <p className="font-medium text-neutral-90">
                                Is a Closing Protection Letter Required ? 
                                </p>
                                <input type="radio" value="Yes" name="isClosingProtectionLetterRequired" 
                                    checked={state.values.isClosingProtectionLetterRequired === 'Yes'}
                                    disabled={readOnly}
                                    //className="disabled:bg-neutral-70"
                                    onChange={onChangeHandler}
                                    
                                /> Yes
                                <input type="radio" value="No" name="isClosingProtectionLetterRequired"
                                    checked={state.values.isClosingProtectionLetterRequired === 'No'} 
                                    disabled={readOnly}
                                    //className="disabled:bg-neutral-70"
                                    onChange={onChangeHandler}
                                /> No
                            </div>
                            
                            <div className="col-span-4 mt-4 mb-4 font-medium text-neutral-90"> Lender&apos;s Insurance Coverage Amount (Loan Amount) *</div>
                            <FormGroup
                                groupClassName="col-span-8 mt-4 mb-4"
                                path='lenderInsuranceCoverageAmount'  
                                type='currency'
                                readOnly={readOnly}               
                                value={state.values.lenderInsuranceCoverageAmount}
                                error={state.errors.lenderInsuranceCoverageAmount}
                                {...fieldActions}
                                onBlur={onBlur}
                            />
                            <Lender lenderType={'lenderPrimary'} colSpan={12} state={state} fieldActions={fieldActions} titleOpinionId={titleOpinionId} sectionId={state.values.sectionId} sectionDetails={state.values.primaryLenderSectionDetails} initialDefaultData={state.values.initialDefaultData}  editMode={!readOnly}/>
                            {state.values.mortgageClausePrimary && state.values.mortgageClausePrimary.label == 'Other' ?
                                <> 
                                    <FormSelect
                                        label="Mortgagee Clause *"
                                        type='select'
                                        options={state.values.mortgageClauseOptions}
                                        required
                                        readOnly={readOnly}
                                        isClearable={false}
                                        isSearchable
                                        groupClassName="col-span-2"
                                        path='mortgageClausePrimary'   
                                        value={state.values.mortgageClausePrimary}
                                        error={state.errors.mortgageClausePrimary}
                                        onChange={async (mortgageClause) => {
                                            saveField(mortgageClause, 'mortgageClausePrimary', state.values.initialDefaultData, state.values.sectionId);
                                            fieldActions.onChange(mortgageClause, 'mortgageClausePrimary');
                                            fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');                        
                                        }}
                                        onBlur={onBlur}                                     
                                    />
                                    <FormGroup
                                        groupClassName="col-span-4 mt-4 mb-4"
                                        path='mortgageClauseOtherPrimary'
                                        readOnly={readOnly}                 
                                        value={state.values.mortgageClauseOtherPrimary}
                                        error={state.errors.mortgageClauseOtherPrimary}
                                        {...fieldActions}
                                        onBlur={onBlur}
                                    />
                                </>
                                :
                                <FormSelect
                                    label="Mortgagee Clause *"
                                    type='select'
                                    options={state.values.mortgageClauseOptions}
                                    required
                                    readOnly={readOnly}
                                    isClearable={false}
                                    isSearchable
                                    groupClassName="col-span-6"
                                    path='mortgageClausePrimary'   
                                    value={state.values.mortgageClausePrimary}
                                    error={state.errors.mortgageClausePrimary}
                                    onChange={async (mortgageClause) => {
                                        saveField(mortgageClause, 'mortgageClausePrimary', state.values.initialDefaultData, state.values.sectionId);
                                        fieldActions.onChange(mortgageClause, 'mortgageClausePrimary');
                                        fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');                        
                                    }}
                                    onBlur={onBlur}             
                                />
                            }
                            
                            <FormGroup
                                label="Loan Number"
                                required
                                maxLength={30}
                                groupClassName="col-span-6 mb-4"
                                path='loanNumberPrimary'  
                                readOnly={readOnly}               
                                value={state.values.loanNumberPrimary}
                                error={state.errors.loanNumberPrimary}
                                {...fieldActions}
                                onBlur={onBlur}
                            />
                            
                            <FormSelect
                                label="Loan Type *"
                                type='select'
                                options={state.values.loanTypeOptions}
                                required
                                readOnly={readOnly}
                                isClearable={false}
                                isSearchable
                                groupClassName="col-span-6"
                                path='loanType'   
                                value={state.values.loanTypePrimary}
                                error={state.errors.loanTypePrimary}
                                onChange={async (loanType) => {
                                    saveField(loanType, 'loanTypePrimary', state.values.initialDefaultData, state.values.sectionId);
                                    fieldActions.onChange(loanType, 'loanTypePrimary');
                                    fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');                        
                                }}
                                onBlur={onBlur}             
                            />
                            {state.values.requiredALTAPrimary.label == 'Other' ?
                                <>
                                    <FormSelect
                                        label="Required ALTA Endorsements"
                                        type='select'
                                        options={state.values.requiredALTAOptions}
                                        required
                                        readOnly={readOnly}
                                        isClearable={false}
                                        isSearchable
                                        isMulti={true}
                                        closeOnSelect={false}
                                        groupClassName="col-span-4"
                                        path='requiredALTAPrimary'   
                                        value={state.values.requiredALTAPrimary}
                                        error={state.errors.requiredALTAPrimary}
                                        onChange={async (requiredALTA) => {
                                            saveField(requiredALTA, 'requiredALTAPrimary', state.values.initialDefaultData, state.values.sectionId);
                                            fieldActions.onChange(requiredALTA, 'requiredALTAPrimary');
                                            fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');                        
                                        }}
                                        onBlur={onBlur}             
                                    />
                                    <FormGroup
                                        groupClassName="col-span-2 mt-4 mb-4"
                                        path='requiredALTAOtherPrimary'    
                                        readOnly={readOnly}             
                                        value={state.values.requiredALTAOtherPrimary}
                                        error={state.errors.requiredALTAOtherPrimary}
                                        {...fieldActions}
                                        onBlur={onBlur}
                                    />

                                </>

                                :
                                <FormSelect
                                    label="Required ALTA Endorsements *"
                                    type='select'
                                    options={state.values.requiredALTAOptions}
                                    required
                                    readOnly={readOnly}
                                    isClearable={false}
                                    isSearchable
                                    isMulti={true}
                                    closeOnSelect={false}
                                    groupClassName="col-span-6"
                                    path='requiredALTAPrimary'   
                                    value={state.values.requiredALTAPrimary}
                                    error={state.errors.requiredALTAPrimary}
                                    onChange={async (requiredALTA) => {
                                        saveField(requiredALTA, 'requiredALTAPrimary', state.values.initialDefaultData, state.values.sectionId);
                                        fieldActions.onChange(requiredALTA, 'requiredALTAPrimary');
                                        fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');                        
                                    }}
                                    onBlur={onBlur}             
                                />
                            }
                        </div>
                        {
                            (readOnly) ? <span className="mb-8" />
                                :(
                                    !state.values.secondaryLenderFlag && !state.values.cashSale ?
                                        <div className='mt-auto col-span-12'>
                                            <div className="flex justify-start items-center pt-8 mt-auto">
                                                <button
                                                    className='py-2 px-2 rounded-full text-white flex items-center focus:outline-none bg-blue-60 disabled:opacity-50'
                                                    onClick={() => addSecondLender()}
                                                >
                                                    <MdAddCircle className="mr-2" size="1.25rem" />
                                                    Add 2nd Lien
                                                </button>
                                            </div>
                                        </div>
                                        :
                                        <div className="col-span-12 grid grid-cols-12 grid-flow-row gap-x-8 gap-y-4 text-sm mb-4">
                                            <div className="col-span-12 border-t-2 border-blueGray-30 mt-4 mb-4"/>
                                            <SectionHeader colSpan={12}>Lender</SectionHeader>
                                            <div className="col-span-12 grid grid-cols-12 grid-flow-row gap-x-8 gap-y-4 text-sm mb-4">
                                                <div className="col-span-6 flex flex-direction:row gap-x-5 gap-y-4">
                                                    <p className="font-medium text-neutral-90">
                                                    Is this a private lender ? 
                                                    </p>
                                                    <input type="radio" value="Yes" name="isPrivateLenderSecondary" 
                                                        checked={state.values.isPrivateLenderSecondary === 'Yes'}
                                                        disabled={readOnly}
                                                        //className="disabled:bg-neutral-70"
                                                        onChange={onChangeHandler}
                                                        
                                                    /> Yes
                                                    <input type="radio" value="No" name="isPrivateLenderSecondary"
                                                        checked={state.values.isPrivateLenderSecondary === 'No'} 
                                                        disabled={readOnly}
                                                        //className="disabled:bg-neutral-70"
                                                        onChange={onChangeHandler}
                                                    /> No
                                                </div>
                                                <div className="col-span-6 flex flex-direction:row gap-x-5 gap-y-4">
                                                    <p className="font-medium text-neutral-90">
                                                    Is a Closing Protection Letter Required ? 
                                                    </p>
                                                    <input type="radio" value="Yes" name="isClosingProtectionLetterRequiredSecondary" 
                                                        checked={state.values.isClosingProtectionLetterRequiredSecondary === 'Yes'}
                                                        disabled={readOnly}
                                                        //className="disabled:bg-neutral-70"
                                                        onChange={onChangeHandler}
                                                        
                                                    /> Yes
                                                    <input type="radio" value="No" name="isClosingProtectionLetterRequiredSecondary"
                                                        checked={state.values.isClosingProtectionLetterRequiredSecondary === 'No'} 
                                                        disabled={readOnly}
                                                        //className="disabled:bg-neutral-70"
                                                        onChange={onChangeHandler}
                                                    /> No
                                                </div>
                                                <div className="col-span-6 flex flex-direction:row gap-x-5 gap-y-4">
                                                    <p className="font-medium text-neutral-90">
                                                    Do you need a separate commitment to this lien ? 
                                                    </p>
                                                    <input type="radio" value="Yes" name="separateCommittment" 
                                                        checked={state.values.separateCommittment === 'Yes'}
                                                        disabled={readOnly}
                                                        //className="disabled:bg-neutral-70"
                                                        onChange={onChangeHandler}
                                                        
                                                    /> Yes
                                                    <input type="radio" value="No" name="separateCommittment"
                                                        checked={state.values.separateCommittment === 'No'} 
                                                        disabled={readOnly}
                                                        //className="disabled:bg-neutral-70"
                                                        onChange={onChangeHandler}
                                                    /> No
                                                </div>
                                                <span className="col-span-4" />
                                                <div className="col-span-4 mt-4 mb-4"> Lender&apos;s Insurance Coverage Amount (Loan Amount) *</div>
                                                <FormGroup
                                                    groupClassName="col-span-8 mt-4 mb-4"
                                                    path='lenderInsuranceCoverageAmountSecondary'  
                                                    type='currency'               
                                                    readOnly={readOnly}
                                                    value={state.values.lenderInsuranceCoverageAmountSecondary}
                                                    error={state.errors.lenderInsuranceCoverageAmountSecondary}
                                                    {...fieldActions}
                                                    onBlur={onBlur}
                                                />
                                                <Lender lenderType={'lenderSecondary'} colSpan={12} state={state} fieldActions={fieldActions} titleOpinionId={titleOpinionId} sectionId={state.values.sectionId} sectionDetails={state.values.secondaryLenderSectionDetails} initialDefaultData={state.values.initialDefaultData} editMode={!readOnly}/>
                                                {state.values.mortgageClauseSecondary.label == 'Other' ?
                                                    <> 
                                                        <FormSelect
                                                            label="Mortgagee Clause *"
                                                            type='select'
                                                            options={state.values.mortgageClauseOptions}
                                                            required
                                                            readOnly={readOnly}
                                                            isClearable={false}
                                                            isSearchable
                                                            groupClassName="col-span-2"
                                                            path='mortgageClauseSecondary'   
                                                            value={state.values.mortgageClauseSecondary}
                                                            error={state.errors.mortgageClauseSecondary}
                                                            onChange={async (mortgageClause) => {
                                                                saveField(mortgageClause, 'mortgageClauseSecondary', state.values.initialDefaultData, state.values.sectionId);
                                                                fieldActions.onChange(mortgageClause, 'mortgageClauseSecondary');
                                                                fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');                        
                                                            }}
                                                            onBlur={onBlur}                                     
                                                        />
                                                        <FormGroup
                                                            groupClassName="col-span-4 mt-4 mb-4"
                                                            path='mortgageClauseOtherSecondary'                 
                                                            readOnly={readOnly}
                                                            value={state.values.mortgageClauseOtherSecondary}
                                                            error={state.errors.mortgageClauseOtherSecondary}
                                                            {...fieldActions}
                                                            onBlur={onBlur}
                                                        />
                                                    </>
                                                    :
                                                    <FormSelect
                                                        label="Mortgagee Clause *"
                                                        type='select'
                                                        options={state.values.mortgageClauseOptions}
                                                        required
                                                        readOnly={readOnly}
                                                        isClearable={false}
                                                        isSearchable
                                                        groupClassName="col-span-6"
                                                        path='mortgageClauseSecondary'   
                                                        value={state.values.mortgageClauseSecondary}
                                                        error={state.errors.mortgageClauseSecondary}
                                                        onChange={async (mortgageClause) => {
                                                            saveField(mortgageClause, 'mortgageClauseSecondary', state.values.initialDefaultData, state.values.sectionId);
                                                            fieldActions.onChange(mortgageClause, 'mortgageClauseSecondary');
                                                            fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');                        
                                                        }}
                                                        onBlur={onBlur}             
                                                    />
                                                }
                                                <FormGroup
                                                    label="Loan Number"
                                                    required
                                                    maxLength={30}
                                                    groupClassName="col-span-6 mb-4"
                                                    path='loanNumberSecondary'                 
                                                    readOnly={readOnly}
                                                    value={state.values.loanNumberSecondary}
                                                    error={state.errors.loanNumberSecondary}
                                                    {...fieldActions}
                                                    onBlur={onBlur}
                                                />
                                                
                                                <FormSelect
                                                    label="Loan Type *"
                                                    type='select'
                                                    options={state.values.loanTypeOptions}
                                                    required
                                                    readOnly={readOnly}
                                                    isClearable={false}
                                                    isSearchable
                                                    groupClassName="col-span-6"
                                                    path='loanTypeSecondary'   
                                                    value={state.values.loanTypeSecondary}
                                                    error={state.errors.loanTypeSecondary}
                                                    onChange={async (loanType) => {
                                                        saveField(loanType, 'loanTypeSecondary', state.values.initialDefaultData, state.values.sectionId);
                                                        fieldActions.onChange(loanType, 'loanTypeSecondary');
                                                        fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');                        
                                                    }}
                                                    onBlur={onBlur}             
                                                />
                                                {state.values.requiredALTASecondary && state.values.requiredALTASecondary.label == 'Other' ?
                                                    <>
                                                        <FormSelect
                                                            label="Required ALTA Endorsements"
                                                            type='select'
                                                            options={state.values.requiredALTAOptions}
                                                            required
                                                            readOnly={readOnly}
                                                            isClearable={false}
                                                            isSearchable
                                                            isMulti={true}
                                                            closeOnSelect={false}
                                                            groupClassName="col-span-4"
                                                            path='requiredALTASecondary'   
                                                            value={state.values.requiredALTASecondary}
                                                            error={state.errors.requiredALTASecondary}
                                                            onChange={async (requiredALTA) => {
                                                                saveField(requiredALTA, 'requiredALTASecondary', state.values.initialDefaultData, state.values.sectionId);
                                                                fieldActions.onChange(requiredALTA, 'requiredALTASecondary');
                                                                fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');                        
                                                            }}
                                                            onBlur={onBlur}             
                                                        />
                                                        <FormGroup
                                                            groupClassName="col-span-2 mt-4 mb-4"
                                                            path='requiredALTAOtherSecondary'                 
                                                            readOnly={readOnly}
                                                            value={state.values.requiredALTAOtherSecondary}
                                                            error={state.errors.requiredALTAOtherSecondary}
                                                            {...fieldActions}
                                                            onBlur={onBlur}
                                                        />

                                                    </>

                                                    :
                                                    <FormSelect
                                                        label="Required ALTA Endorsements *"
                                                        type='select'
                                                        options={state.values.requiredALTAOptions}
                                                        required
                                                        readOnly={readOnly}
                                                        isClearable={false}
                                                        isSearchable
                                                        isMulti={true}
                                                        closeOnSelect={false}
                                                        groupClassName="col-span-6"
                                                        path='requiredALTASecondary'   
                                                        value={state.values.requiredALTASecondary}
                                                        error={state.errors.requiredALTASecondary}
                                                        onChange={async (requiredALTA) => {
                                                            saveField(requiredALTA, 'requiredALTASecondary', state.values.initialDefaultData, state.values.sectionId);
                                                            fieldActions.onChange(requiredALTA, 'requiredALTASecondary');
                                                            fieldActions.onChange({ label: '...', value: ''}, 'requestedBy');                        
                                                        }}
                                                        onBlur={onBlur}             
                                                    />
                                                }
                                            </div>
                                            <div className='mt-auto col-span-12'>
                                                <div className="flex justify-start items-center pt-8 mt-auto">
                                                    <button
                                                        className='py-2 px-2 rounded-full text-white flex items-center focus:outline-none bg-blue-60 disabled:opacity-50'
                                                        onClick={() => removeSecondLender()}
                                                    >
                                                        <MdRemoveCircle className="mr-2" size="1.25rem" />
                                                        Remove 2nd Lien
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                )
                        }
                    </>
                }
                
            </div>
            
        </div>
    );
};

export default TitleInsuranceApplication;